import { Navigate } from 'react-router-dom'

import {
  CompetenciesEmployeesRootPageDocument,
  CompetenciesEmployeesRootPageQuery,
} from 'generated/resources/graphql'

import NotFound from 'modules/core/components/NotFound'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { useStrictParams } from 'modules/core/hooks/useStrictParams'

import { usePagination } from 'modules/competencies/hooks/usePagination'
import CollectionsBootstrap from 'modules/competencies/lib/EmployeesCollectionsBootstrap'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'

import Component from './Competencies'

interface ContainerProps {
  data?: CompetenciesEmployeesRootPageQuery
  hasCompetencies: boolean
  hasNextPage: boolean
  hasPreviousPage: boolean
  isEmpty: boolean
  onNextPage: () => void
  onPreviousPage: () => void
}

const Container = ({
  data,
  hasCompetencies,
  hasNextPage,
  hasPreviousPage,
  isEmpty,
  onNextPage,
  onPreviousPage,
}: ContainerProps) => {
  const { navigation } = useNavigation()

  const canManageCompetencies = data?.competencies._policies.competenciesManage.can ?? false

  if (!hasCompetencies && canManageCompetencies)
    return <Navigate replace to={navigation.discover.marketplaceModules.fullPath()} />

  if (!hasCompetencies) return <NotFound />

  return (
    <Component
      assessmentGroup={data?.competencies.assessmentGroupsConnection.edges[0]?.node}
      isEmpty={isEmpty}
      hasNextPage={hasNextPage}
      hasPreviousPage={hasPreviousPage}
      onNextPage={onNextPage}
      onPreviousPage={onPreviousPage}
    />
  )
}

const RootPage = () => {
  const { id: employeeId } = useStrictParams<'id'>()
  const {
    company: { id: companyId },
  } = useCurrentSession()

  const { pageNumber, handleNextPage, handlePreviousPage } = usePagination()

  const { loading, data, error } = useQuery(CompetenciesEmployeesRootPageDocument, {
    variables: {
      companyId: companyId,
      featureIds: ['competencies_core'],
      targetEmployeeIds: [employeeId],
      offsetPage: pageNumber,
    },
  })

  const pageInfo = data?.competencies.assessmentGroupsConnection.pageInfo
  const totalCount = data?.competencies.assessmentGroupsConnection.totalCount ?? 0
  const isEmpty = totalCount === 0
  const featureAvailabilities = data?.companies?.featureAvailabilitiesConnection?.nodes ?? []
  const hasCompetencies =
    featureAvailabilities.find((feature) => feature.id === 'competencies_core')?.available ?? false

  return (
    <ResponsivePage.Body loading={loading} error={error} variant="full">
      <CollectionsBootstrap>
        <Container
          data={data}
          hasCompetencies={hasCompetencies}
          hasNextPage={pageInfo?.hasNextPage ?? false}
          hasPreviousPage={pageInfo?.hasPreviousPage ?? false}
          isEmpty={isEmpty}
          onNextPage={() => handleNextPage(totalCount)}
          onPreviousPage={handlePreviousPage}
        />
      </CollectionsBootstrap>
    </ResponsivePage.Body>
  )
}

export default RootPage
